import type {
    CreateObjectGroup,
    CreateObjectInput,
    CreateObjectsRequest,
    CreateStationRequest,
    StationLocationAddress,
    StationOpeningHour,
} from '../../../api/station/stationApiTypes';
import type { OpeningHourEntry } from '../../../types/locations';
import {
    type StationCreationForm,
    parseCount,
    resolvedProductRows,
    stationNameOf,
} from './stationCreationForm';

// Form state → the target-model write contract. Pure, so the mapping is
// testable without mounting the wizard — the same reason the step validation
// is a pure function.

/**
 * The access declaration, folded into the station's description.
 *
 * The wizard captures which lock is (to be) installed and a free-text hardware
 * note, inventory-style: ops provisions it later, the tenant does not
 * self-configure IoT (spec §4). **There is no schema field for either** — the
 * `thing` model records only name/endpoint/last_seen, and adding a home for
 * them is still an open item on #2134.
 *
 * So they go into `description`, which is a real contract field, rather than
 * being dropped. Silently discarding what an operator typed is worse than an
 * imperfect home for it, and ops needs to read it to make the callback the
 * wizard promises. Revisit when the installed-hardware fields land.
 */
export const buildStationDescription = (form: StationCreationForm): string => {
    const parts = [form.description.trim()];
    if (form.accessType !== 'none') {
        parts.push(`Access: ${form.accessType}`);
    }
    // The two answers ops acts on: whether the hardware is already there, or
    // whether we still have to propose and supply it. Recorded even when the
    // free-text detail is empty — "locks still needed" IS the information.
    if (form.lockStatus) {
        parts.push(`Locks: ${form.lockStatus}`);
    }
    if (form.lockDeclaration.trim()) {
        parts.push(`Lock: ${form.lockDeclaration.trim()}`);
    }
    if (form.controlUnitStatus) {
        parts.push(`Control unit: ${form.controlUnitStatus}`);
    }
    if (form.installedHardware.trim()) {
        parts.push(`Hardware: ${form.installedHardware.trim()}`);
    }
    return parts.filter((part) => part.length > 0).join('\n');
};

/** A new location's address, as both variants send it. */
const newLocationAddressOf = (form: StationCreationForm): StationLocationAddress => ({
    street: form.newLocationStreet.trim(),
    ...(form.newLocationHouseNumber.trim()
        ? { houseNumber: form.newLocationHouseNumber.trim() }
        : {}),
    postalCode: form.newLocationPostalCode.trim(),
    city: form.newLocationCity.trim(),
    ...(form.newLocationDistrict?.trim() ? { district: form.newLocationDistrict.trim() } : {}),
    // Only a complete pin is sent; the step refuses a half one.
    ...(form.newLocationGeoLat != null && form.newLocationGeoLng != null
        ? { geoLat: form.newLocationGeoLat, geoLng: form.newLocationGeoLng }
        : {}),
});

/**
 * Opening hours as the station endpoints take them — create and the location
 * edit alike: one `times` object per weekday. The server rejects anything else
 * (`validateOpeningHoursPayload`, new-backend `lib/location/locationWrite.ts`);
 * the location edit once sent `times` as a list and every save with hours 400'd.
 */
export const toStationOpeningHours = (
    entries: OpeningHourEntry[],
): StationOpeningHour[] => entries.map((entry) => ({
    weekday: entry.weekday,
    times: { fromHour: entry.times.fromHour, toHour: entry.times.toHour },
}));

/** The location's opening hours, when any were entered. */
const openingHoursOf = (
    form: StationCreationForm,
): Pick<CreateStationRequest, 'openingHours'> => (
    form.newLocationOpeningHours.length > 0
        ? { openingHours: toStationOpeningHours(form.newLocationOpeningHours) }
        : {}
);

/**
 * The location's service scope is NOT built here. The backend derives it from
 * the Product (`useCasesOfProduct`), because `compartment_types` has also
 * carried locker sizes ("L") and a scope assembled client-side can pass a
 * non-empty check while leaving the location invisible to every booking flow.
 * The wizard sends the Product; the backend decides what that means.
 */
export const buildCreateStationRequest = (
    form: StationCreationForm,
): CreateStationRequest => {
    // A room-station is one object that IS the station, so exactly one
    // position and no buffer (spec §3a Shape B).
    const isRoom = form.stationType === 'room';
    const doorCount = isRoom ? 1 : (parseCount(form.positionCount) ?? 0);
    const bufferDoorsCount = isRoom ? 0 : (parseCount(form.bufferCount) ?? 0);

    const description = buildStationDescription(form);

    return {
        name: stationNameOf(form),
        ...(form.locationMode === 'existing'
            ? { locationId: form.existingLocationId }
            : {
                newLocation: {
                    name: form.newLocationName.trim(),
                    /*
                     * The FIRST product's use cases seed the new location's
                     * service scope; the others widen it moments later.
                     *
                     * `createStation` derives the scope from one Product, and a
                     * station may now carry several (#2280). That is not a gap:
                     * `ensureLocationOffersProduct` runs per product inside the
                     * objects transaction and UNIONS `services` rather than
                     * replacing it (`lib/location/locationWrite.ts:340`), so by
                     * the time the objects exist the location offers every one
                     * of their use cases. Read, not assumed — it is the reason
                     * this stayed a single field.
                     */
                    productId: form.productRows[0]?.productId.trim() ?? '',
                    address: newLocationAddressOf(form),
                },
            }),
        // Sent on both branches: a new location needs them, and an existing one
        // that has NONE is closed on all seven days — the backend writes them
        // only in that case and never over hours that already exist.
        ...openingHoursOf(form),
        doorCount,
        bufferDoorsCount,
        // `containerType` is deliberately not sent: the wizard has no field for
        // it and the backend defaults it. Shipping a magic value from here is
        // exactly what the contract tells us not to do.
        ...(description ? { description } : {}),
    };
};

/**
 * The house variant's request (plan `room-houses-and-combining-rooms.md` §4
 * row 1): no positions, no buffer, and — for a new location — a scope from the
 * tenant's room use cases instead of a Product, because a house is created
 * empty. The backend accepts only the room family there, so `roomUseCases` is
 * the tenant's `venue`/`apartment` services and nothing else.
 */
export const buildCreateHouseRequest = (
    form: StationCreationForm,
    roomUseCases: string[],
): CreateStationRequest => {
    const description = form.description.trim();
    return {
        name: stationNameOf(form),
        ...(form.locationMode === 'existing'
            ? { locationId: form.existingLocationId }
            : {
                newLocation: {
                    name: form.newLocationName.trim(),
                    useCases: roomUseCases,
                    address: newLocationAddressOf(form),
                },
            }),
        // The location's hours: a new location needs them, and an existing one
        // gets them only if it has none (see `buildCreateStationRequest`).
        ...openingHoursOf(form),
        doorCount: 0,
        bufferDoorsCount: 0,
        // The one value that tells a house from a station; the backend has no
        // other column for it (`stationEdit.ts`, `HOUSE_CONTAINER_TYPE`).
        containerType: 'house',
        ...(description ? { description } : {}),
    };
};

/**
 * The object at one position, named and described by the objects step.
 *
 * Buffer positions are the technical/held-back ones, and they are held back
 * from the END of the range: `door_count` numbers the whole cabinet, and the
 * controller rejects a position beyond it. So N bookable objects take positions
 * 1..N and the buffer keeps the tail.
 */
const objectAtPosition = (
    form: StationCreationForm,
    index: number,
): CreateObjectInput => {
    const prefix = form.objectNamePrefix.trim();
    const manualName = form.objectNames[index]?.trim();
    const name = form.objectNaming === 'manual'
        ? (manualName || `${index + 1}`)
        : `${prefix} ${index + 1}`.trim();

    // In auto mode the objects are alike, so one volume and one note apply
    // to all of them; manual mode carries per-unit values.
    const isAuto = form.objectNaming === 'auto';
    const serial = isAuto ? '' : form.objectSerials[index]?.trim();
    const note = (isAuto ? form.sharedObjectNote : form.objectNotes[index] ?? '').trim();
    const volume = parseCount(
        (isAuto ? form.sharedObjectVolume : form.objectVolumes[index] ?? '').trim(),
    );

    return {
        name,
        door: index + 1,
        // Declared per position; ops sets the real mechanism at connection
        // time, so the wizard's declaration is what we send (spec §4).
        doorMechanism: form.accessType,
        ...(serial ? { serialNumber: serial } : {}),
        ...(volume !== null ? { volumeCm3: volume } : {}),
        ...(note ? { note } : {}),
    };
};

/**
 * The objects to create, in the shape this station actually needs.
 *
 * ── Why ONE product still sends the legacy shape ───────────────────────────
 *
 * `groups` is new; the old handler reads only `productId` + `objects` and
 * answers 400 to anything else. Admin-ui and the backend deploy independently
 * (Amplify vs. the ECS action, both triggered by the same merge), so there is a
 * window in which the new wizard talks to the old handler — and a wizard that
 * cannot create ANY station during it is a bad trade for a uniform cabinet that
 * has no need of groups at all.
 *
 * So a single-product station sends what it has always sent, and only a genuine
 * multi-product station needs the new backend. That failure is acceptable: it
 * is a capability nobody had five minutes ago, and it fails loudly.
 *
 * This deliberately reverses "one shape from one caller", which was the first
 * version of this function. That argument is about tidiness; this one is about
 * whether station creation works during a deploy. `objectGroups.test.ts` pins
 * that the backend reduces both shapes to the same thing, so the two paths
 * cannot drift into different behaviour.
 */
export const buildCreateObjectsRequest = (
    form: StationCreationForm,
    stationId: string,
): CreateObjectsRequest => {
    const rows = resolvedProductRows(form);

    /*
     * Rows carve the positions into CONSECUTIVE blocks: 5× L takes 1–5, 5× M
     * takes 6–10. Consecutive rather than interleaved because a cabinet is
     * built that way — the L compartments are next to each other — and because
     * the position numbers are what the operator will read off the doors.
     *
     * `resolvedProductRows` has already established that the counts total the
     * bookable objects, so the blocks tile the range exactly; there is no
     * leftover tail to decide about here.
     */
    let next = 0;
    const groups: CreateObjectGroup[] = (rows ?? []).map((row) => {
        const objects = Array.from(
            { length: row.count },
            (_, offset) => objectAtPosition(form, next + offset),
        );
        next += row.count;
        return {
            // Linked in the SAME transaction that creates the objects. A
            // separate link call can fail after the objects are written,
            // leaving a station whose objects fulfil nothing.
            productId: row.productId,
            objects,
        };
    });

    // Always a pool. See LinkProductStep: `exclusive` is a second Product over
    // the same objects, not a station-creation choice.
    const mode = 'any_of' as const;

    /*
     * The rows did not resolve. Step 3 refuses such a form, so this should be
     * unreachable; if it ever is reached, BOTH the old and the new handler
     * answer the same 400 ("objects must be a non-empty array") rather than
     * creating anything. Objects that fulfil no Product are configured and
     * unbookable — the outcome the required-Product rule exists to prevent — so
     * a loud refusal is the only acceptable behaviour here.
     */
    if (!rows || rows.length === 0) {
        return { stationId, mode, objects: [] };
    }

    const only = rows.length === 1 ? rows[0] : null;
    if (only) {
        return {
            stationId,
            mode,
            // Linked in the SAME transaction that creates the objects. A
            // separate link call can fail after the objects are written,
            // leaving a station whose objects fulfil nothing.
            productId: only.productId,
            objects: groups[0]?.objects ?? [],
        };
    }

    return { stationId, mode, groups };
};
